import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

// ── Shell scroll ─────────────────────────────────────────────────────────
// The shell's page panel (InsetShell.Panel) is the ONE scroll container:
// the document never scrolls. Everything that used to talk to the window
// scroll — react-router's <ScrollRestoration>, a virtualizer's scroll
// element, a "back to top" — talks to the panel instead, found through the
// attribute the panel carries (a DOM lookup, not a context, so it works
// from anywhere under the shell, federated pages included).

/** The attribute InsetShell.Panel carries. */
export const SHELL_SCROLLER_ATTR = 'data-shell-scroller';

/** The shadow a band (the top bar, the rail's brand band) casts while the
 * content under it is scrolled off its top. */
export const SCROLLED_SHADOW = {
    base: '0 2px 8px -2px rgb(0 0 0 / 0.12)',
    _dark: '0 2px 8px -2px rgb(0 0 0 / 0.6)',
} as const;

/** Clips a band's scroll shadow to the side it falls on — the blur would
 * otherwise bleed sideways across the rail's border. */
export const SHADOW_CLIP_BELOW = 'inset(0 0 -12px 0)';
export const SHADOW_CLIP_ABOVE = 'inset(-12px 0 0 0)';

/** The same, cast upward — a band below content that continues under it
 * (the rail's footer). */
export const SCROLLED_SHADOW_UP = {
    base: '0 -2px 8px -2px rgb(0 0 0 / 0.12)',
    _dark: '0 -2px 8px -2px rgb(0 0 0 / 0.6)',
} as const;

/** The shell's scroll container, or null outside the shell (tests, error
 * screens). Cheap enough to call per render — one attribute lookup. */
export function getShellScroller(): HTMLElement | null {
    if (typeof document === 'undefined') return null;
    return document.querySelector<HTMLElement>(`[${SHELL_SCROLLER_ATTR}]`);
}

/** Scroll the shell's panel to the top (no-op outside the shell). */
export function scrollShellToTop() {
    getShellScroller()?.scrollTo({ top: 0 });
}

// Mirrors react-router's <ScrollRestoration> storage: positions keyed by
// location.key, persisted for the tab's lifetime so a reload lands where
// it was.
const STORAGE_KEY = 'inset-shell-scroll-positions';

type Positions = Record<string, number>;

function loadPositions(): Positions {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Positions) : {};
    } catch {
        return {};
    }
}

function savePositions(positions: Positions) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    } catch {
        // Storage full or blocked: restoration degrades to this page load.
    }
}

/** The element id a hash names. A malformed fragment (`#a%`) makes
 * decodeURIComponent throw — this runs on every location commit, so fall
 * back to the raw fragment instead of taking the render down. */
function decodeHashId(hash: string): string {
    const raw = hash.slice(1);
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
}

/** react-router's <ScrollRestoration> for the panel scroller: restore the
 * panel's offset on Back/Forward (and reload), reset it to the top on
 * pushes, scroll a hash target into view. Mount it once inside the router,
 * as a LATER sibling of the shell; it renders nothing. Same timing as the
 * router's own — the location commits once loaders settle, so the restored
 * offset lands on the new page's content (pages that stream in later stay
 * at the clamped offset, as before).
 *
 * Why after the shell: the restore runs in a layout effect, and React runs
 * siblings' layout effects in tree order — so mounted after the shell, it
 * runs after the incoming page's own mount effects. That matters because a
 * TanStack virtualizer scrolls its scroll element to its initial offset
 * (0) when it first sees it; mounted before the shell, the restore would
 * land first and the page would zero it. */
export function InsetShellScrollRestoration() {
    const location = useLocation();
    const navigationType = useNavigationType();
    // Loaded lazily once (sessionStorage is sync but not free) and written
    // back on pagehide, like the router's.
    const positions = useRef<Positions | null>(null);
    const getPositions = () => {
        positions.current ??= loadPositions();
        return positions.current;
    };

    // Record the live offset against the CURRENT location's key. ONE
    // listener for the component's life, reading the key through a ref
    // that the restore effect below rebinds BEFORE it moves the scroller:
    // the scroll events a navigation fires (the reset to top, the browser's
    // own clamp to a shorter page) land after the commit and must count
    // against the new location — a per-key listener swapped in a passive
    // effect can run too late and let them zero the page being left.
    const key = location.key;
    const keyRef = useRef(key);
    useEffect(() => {
        const scroller = getShellScroller();
        if (!scroller) return;
        const record = () => {
            getPositions()[keyRef.current] = scroller.scrollTop;
        };
        scroller.addEventListener('scroll', record, { passive: true });
        return () => scroller.removeEventListener('scroll', record);
    }, []);

    useEffect(() => {
        const persist = () => {
            if (positions.current) savePositions(positions.current);
        };
        window.addEventListener('pagehide', persist);
        return () => window.removeEventListener('pagehide', persist);
    }, []);

    // Restore / reset when the location changes. Layout effect: before the
    // new page paints, so Back doesn't flash the top of the page first.
    // Children's layout effects run before this one, so the page's own
    // layout is committed by the time the offset is applied.
    const hash = location.hash;
    useLayoutEffect(() => {
        keyRef.current = key;
        const scroller = getShellScroller();
        if (!scroller) return;
        if (navigationType === 'POP') {
            const saved = getPositions()[key];
            if (saved !== undefined) {
                scroller.scrollTop = saved;
                return;
            }
        }
        if (hash) {
            const target = document.getElementById(decodeHashId(hash));
            if (target) {
                target.scrollIntoView();
                return;
            }
        }
        scroller.scrollTop = 0;
    }, [key, hash, navigationType]);

    return null;
}
